import './index.css'

import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import React, { useEffect, useRef, useState } from 'react'
import { Outlet, useParams } from 'react-router-dom'

import { ARTIST } from '@disco/constants/ItemTypes'
import {
  clientLibraryArtistsLoad,
  clientLibraryArtistsLoadNext,
  clientLibraryArtistUpdate,
  clientLibraryFeaturedArtistsSet,
} from '@disco/state-management/actions/client-library-artist-actions'
import {
  getClientLibrary,
  getClientLibraryArtists,
} from '@disco/state-management/selectors/client-libraries'
import { useComposerInsteadOfArtist } from '@disco/utils/composerInsteadOfArtist'

import type { ArtistId } from 'types/artist'
import type { ClientLibrary, ClientLibraryArtist } from 'types/client-library'
import type { State } from 'types/redux'

import emptyArtists1x from 'assets/images/empty-artists.png'
import emptyArtists2x from 'assets/images/empty-artists@2x.png'
import emptySearch1x from 'assets/images/empty-search.png'
import emptySearch2x from 'assets/images/empty-search@2x.png'

import ArtistGridItem from 'components/common/artist-grid-item'
import Row from 'components/common/row'
import DocumentHeadTags from 'components/document-head-tags'
import { Tab, Tabs } from 'components/ui'
import Button from 'components/ui/button'
import Checkbox from 'components/ui/checkbox'
import Draggable from 'components/ui/draggable'
import EmptyState from 'components/ui/empty-state'
import Grid from 'components/ui/grid'
import InfiniteScroll from 'components/ui/infinite-scroll'
import Input from 'components/ui/input'
import PageContainer from 'components/ui/page-container'
import Sortable from 'components/ui/sortable'
import Tooltip from 'components/ui/tooltip'

import { useDispatch, useSelector } from 'hooks/use-redux'

const LibrariesLibraryArtists = (): React.ReactNode => {
  const params = useParams<{ libraryId: string }>()
  const dispatch = useDispatch()
  const libraryId: number = parseInt(params.libraryId!, 10)
  const library: ClientLibrary = useSelector((state: State) =>
    getClientLibrary(state, libraryId),
  )
  const artists = useSelector((state: State) =>
    getClientLibraryArtists(state, libraryId),
  )
  const typeData = useComposerInsteadOfArtist()
  const [selectedTab, setSelectedTab] = useState(0)
  const [query, setQuery] = useState('')
  const [isLoadingNext, setIsLoadingNext] = useState(false)
  const [isSearching, setIsSearching] = useState(false)

  const fetchArtists = () => {
    if (selectedTab === 0) {
      dispatch(clientLibraryArtistsLoad(libraryId, query))
    }

    if (selectedTab === 1) {
      setQuery('')
      dispatch(clientLibraryArtistsLoad(libraryId, '', true))
    }

    setIsLoadingNext(false)
  }

  useEffect(fetchArtists, [selectedTab])
  useEffect(() => {
    if (artists?.invalidate) {
      fetchArtists()
    }
  }, [artists?.invalidate])
  const debouncedQuery = useRef(
    debounce(query => {
      setIsSearching(false)
      dispatch(clientLibraryArtistsLoad(libraryId, query))
    }, 300),
  ).current

  const updateSearchQuery = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoadingNext(false)
    setIsSearching(true)
    setQuery(event.target.value)
    debouncedQuery(event.target.value)
  }

  const toggleIsFeatured = (artistId: number) => (_, value: boolean) => {
    dispatch(
      clientLibraryArtistUpdate(libraryId, artistId, {
        is_featured: value,
      }),
    )
  }

  const setFeaturedArtists = item => {
    const order: ArtistId[] = item?.order.map(Number)

    if (!isEqual(artists?.list, order)) {
      dispatch(clientLibraryFeaturedArtistsSet(libraryId, order))
    }
  }

  const loadNext = (next: string) => {
    setIsLoadingNext(true)
    dispatch(clientLibraryArtistsLoadNext(libraryId, next))
  }

  const renderContent = (artists: {
    loading: boolean
    loaded: boolean
    nextPage: string | null
    items: Record<ArtistId, ClientLibraryArtist>
    list: ArtistId[]
  }) =>
    !isLoadingNext && artists.loading ? 'Loading...' : renderArtists(artists)

  const renderArtists = (artists: {
    loading: boolean
    loaded: boolean
    nextPage: string | null
    items: Record<ArtistId, ClientLibraryArtist>
    list: ArtistId[]
  }) => {
    const items: ClientLibraryArtist[] = artists.list.map(
      (id: ArtistId) => artists.items[id],
    )
    return artists.loaded && artists.list.length === 0 ? (
      renderEmptyState()
    ) : (
      <InfiniteScroll next={artists.nextPage} loadNext={loadNext}>
        {selectedTab === 0 && <Grid size="small">{items.map(renderItem)}</Grid>}
        {selectedTab === 1 && (
          <Sortable
            accept={[ARTIST]}
            id={`${libraryId}-featured-artists`} // TODO: Fix reducer for featured artists
            items={items.filter(item => item['is_featured'])}
            renderItem={renderItem}
            onDrop={setFeaturedArtists}
          >
            {items => <Grid size="small">{items}</Grid>}
          </Sortable>
        )}
      </InfiniteScroll>
    )
  }

  const renderItem = (artist: ClientLibraryArtist) => {
    if (!artist) return null
    const tooltip: string = artist['is_featured']
      ? 'Remove from featured'
      : `Feature this ${typeData.value}`
    const item: React.ReactNode = (
      <ArtistGridItem
        key={artist['id']}
        image={artist['image_url']}
        imageParams={{
          crop: 'faces',
        }}
        title={artist['name']}
        status={artist['is_featured'] ? 'Featured' : undefined}
        overlayActions={
          <>
            <Tooltip content={`Edit ${typeData.value}`}>
              <Button
                appearance="icon"
                compact
                icon="edit"
                link={`/catalogs/${libraryId}/artists/${artist['id']}`}
                iconStyle={{ color: '#ffffff' }}
              />
            </Tooltip>
            <Tooltip content={tooltip}>
              <Checkbox
                checked={artist['is_featured']}
                onChange={toggleIsFeatured(artist['id'])}
                className="featured-checkbox"
              />
            </Tooltip>
          </>
        }
      />
    )
    return selectedTab === 0 ? (
      item
    ) : (
      <Draggable key={artist['id']} id={artist['id']} type={ARTIST}>
        {item}
      </Draggable>
    )
  }

  const renderEmptyState = () =>
    !isSearching && query === '' ? (
      <EmptyState>
        <img
          srcSet={`${emptyArtists1x}, ${emptyArtists2x} 2x`}
          width="194"
          height="185"
        />
        {selectedTab === 0 && (
          <>
            <p>
              No {typeData.value}s here yet. To add {typeData.value}s, select
              content on the “Content” page.
            </p>
            <Button
              appearance="primary"
              caption="Add content"
              link={`/catalogs/${libraryId}/content`}
            />
          </>
        )}
        {selectedTab === 1 && (
          <p>
            No {typeData.value}s here yet! To add {typeData.value}s, select them
            in the “All {typeData.value}s” tab.
          </p>
        )}
      </EmptyState>
    ) : (
      <EmptyState>
        <img
          srcSet={`${emptySearch1x}, ${emptySearch2x} 2x`}
          width="177"
          height="160"
        />
        <p>
          Sorry! We couldn’t find any {typeData.value}s for that search. Try
          another query, or to add more {typeData.value}s, add more content in
          the Content page.
        </p>
        <Button
          appearance="primary"
          caption="Add content"
          link={`/catalogs/${libraryId}/content`}
        />
      </EmptyState>
    )

  return (
    <PageContainer>
      <PageContainer.Section
        title={`${typeData.title}s`}
        description={`The ${typeData.value}s below are ${typeData.value}s that will be featured on this Catalog, based on the content you’ve set up in the Content step.`}
      >
        {library && !library['loading'] && (
          <DocumentHeadTags
            title={`${library['name']} | ${typeData.title}s | DISCO`}
          />
        )}
        <Tabs
          className="Tabs--modal"
          index={selectedTab}
          onChange={(index: number) => setSelectedTab(index)}
        >
          <Tab label={`All ${typeData.value}s`}>
            <Row columnsSize={[3]}>
              <div>
                <Input
                  icon="search"
                  placeholder="Search"
                  value={query}
                  onChange={updateSearchQuery}
                />
              </div>
            </Row>
            {artists && renderContent(artists)}
          </Tab>
          <Tab label={`Featured ${typeData.value}s`}>
            {artists && renderContent(artists)}
          </Tab>
        </Tabs>
        <Outlet />
      </PageContainer.Section>
    </PageContainer>
  )
}

export default LibrariesLibraryArtists
